<template>
  <sidebar-overlay class="settings">
    <h2 class="sidebarTitle">Reactions</h2>
    <ul class="content">
      <li v-for="emote in emotes" :key="emote.icon">
        <button :class="emote.icon" @click="send(emote.name)">
          <app-icon :icon="emote.icon + '-control'" aria-hidden="true" />
          <span class="visuallyHidden">{{ emote.description }}</span>
        </button>
        <span class="key">
          <code>{{ emote.code[0] }}</code>
          <code>{{ emote.code[1] }}</code>
        </span>
      </li>
      <li class="hand">
        <button class="btn-border btn-question" @click="handleHand">
          {{ handLabel }}
        </button>
        <span class="key">
          <code>h</code>
          <code>u</code>
        </span>
      </li>
    </ul>
  </sidebar-overlay>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useUsersStore } from '@/store/pinia/users';
import AppIcon from '@/components/AppIcon';
import SidebarOverlay from '@/components/SidebarOverlay';
const sidebarStore = useSidebarStore();
const usersStore = useUsersStore();
const { $ee, $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;

const emotes = [
  {
    name: 'yes',
    icon: 'yes',
    description: 'thumbs up',
    code: ['e', 'y'],
  },
  {
    name: 'no',
    icon: 'no',
    description: 'thumbs down',
    code: ['e', 'n'],
  },
  {
    name: 'wave',
    icon: 'wave',
    description: 'wave',
    code: ['e', 'w'],
  },
  {
    name: 'what',
    icon: 'what',
    description: 'question',
    code: ['e', 'q'],
  },
  {
    name: 'eek',
    icon: 'eek',
    description: 'insight',
    code: ['e', 'i'],
  },
  {
    name: 'smile',
    icon: 'smile',
    description: 'happy',
    code: ['e', 'h'],
  },
  {
    name: 'grr',
    icon: 'grr',
    description: 'angry',
    code: ['e', 'a'],
  },
  {
    name: 'sad',
    icon: 'sad',
    description: 'sad',
    code: ['e', 's'],
  },
  {
    name: 'uhh',
    icon: 'uhh',
    description: 'uhh...',
    code: ['e', 'u'],
  },
  {
    name: 'confetti',
    icon: 'confetti',
    description: 'celebrate',
    code: ['e', 'c'],
  },
  {
    name: 'heart',
    icon: 'heart',
    description: 'love',
    code: ['e', 'l'],
  },
  {
    name: 'star',
    icon: 'star',
    description: 'star',
    code: ['e', 'x'],
  },
  {
    name: 'roll',
    icon: 'roll',
    description: 'rolling on the floor laughing',
    code: ['e', 'r'],
  },
  {
    name: 'flex',
    icon: 'flex',
    description: 'flexing',
    code: ['e', 'f'],
  },
  {
    name: 'thoughtful',
    icon: 'thoughtful',
    description: 'thoughtful',
    code: ['e', 't'],
  },
];

let autoCloseTimeout = false;

onMounted(setAutoClose);
onBeforeUnmount(() => {
  clearTimeout(autoCloseTimeout);
  autoCloseTimeout = false;
});

const { me } = storeToRefs(usersStore);
const { current: currentSidebar } = storeToRefs(sidebarStore);
const handLabel = computed(() => (me.value.hand ? 'lower hand' : 'raise hand'));

function send(emote) {
  $ee.emit('nM:emote:key', emote);
  setAutoClose();
}

function handleHand() {
  if (me.value.hand) {
    $meetingmanager.handDown();
  } else {
    $meetingmanager.handUp();
  }
  setAutoClose();
}

function setAutoClose() {
  clearTimeout(autoCloseTimeout);
  autoCloseTimeout = setTimeout(() => {
    if (currentSidebar.value.name === 'emoteGrid') {
      sidebarStore.closeForTimedTemp('emoteGrid');
    }
  }, 30000);
}
</script>

<style lang="scss" scoped>
ul {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  list-style: none;
  gap: rem(27px);
  margin: rem(38px) rem(63px);
}
li {
  text-align: center;
}
li.hand {
  grid-column-start: 1;
  grid-column-end: 4;
  button {
    height: auto;
    padding: 0.5em 0;
  }
}
:deep(svg) {
  width: 100%;
  height: rem(40px);
}
code {
  border: 1px solid var(--c__quiet-button);
  color: var(--c__placeholder);
  display: inline-block;
  border-radius: 2px;
  text-transform: uppercase;
  font-size: rem(10px);
  height: rem(12px);
  width: rem(12px);
  line-height: rem(9px);
  text-align: center;
  font-family: $body-font;
}

.separator {
  color: var(--c__placeholder);
}

button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: 100%;
  @each $button in $emotes {
    &.#{$button} {
      color: var(--c__emote-#{$button});
    }
  }
}
button.confetti {
  :deep(.blue) {
    color: var(--c__blue);
  }
  :deep(.green) {
    color: var(--c__green);
  }
  :deep(.yellow) {
    color: var(--c__yellow);
  }
  :deep(.orange) {
    color: var(--c__orange);
  }
  :deep(.red) {
    color: var(--c__red);
  }
}

button.thoughtful,
button.flex,
button.roll {
  :deep(svg) {
    & * {
      vector-effect: non-scaling-stroke;
      stroke-width: 1.5;
    }
  }
}
</style>
